import {User} from "../../../../../api/accessControl/User.ts";
import {useEffect, useState} from "react";
import {BackendApi} from "../../../../../api/api.ts";
import {BankAccountType, CustomerBankAccount} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import {CustomerBankAccountRepository} from "../../../../../api/customerBankAccounts/CustomerBankAccountRepository.ts";
import {BankAccountCard} from "../../../../../components/specific/BankAccountCard/BankAccountCard.tsx";
import Shell from "../../../../../components/specific/Shell.tsx";
import {CreditCardAccountCard} from "../../../../../components/specific/BankAccountCard/CreditCardAccountCard.tsx";
import {AddCreditCardModal, Institution} from "./AddCreditCardModal.tsx";
import {AddCardTile} from "./AddCardTile.tsx";
import {BanksPageSectionHeader} from "./BanksPageSectionHeader.tsx";
import {SavingsAccountPickerDrawer} from "./SavingsAccountPickerDrawer.tsx";
import {AddSavingsAccountBalanceModal} from "./AddSavingsAccountBalanceModal.tsx";
import {BankAccount as Relation} from "../entrepreneurSettingsPage/BankAccountsTable.tsx";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {toSelectableSavingsAccountRelations} from "./savingsRelations.ts";

export default function BanksPage({currentUser}: { currentUser: User }) {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isAddCreditCardModalOpen, setIsAddCreditCardModalOpen] = useState(false);
    const [bankAccounts, setBankAccounts] = useState<Array<CustomerBankAccount>>([]);
    const [institutions, setInstitutions] = useState<Array<Institution>>([]);
    const [isSavingsPickerOpen, setIsSavingsPickerOpen] = useState(false);
    const [isSavingsPickerLoading, setIsSavingsPickerLoading] = useState(false);
    const [isSavingsBalanceModalOpen, setIsSavingsBalanceModalOpen] = useState(false);
    const [relations, setRelations] = useState<Array<Relation>>([]);
    const [selectedRelation, setSelectedRelation] = useState<Relation | null>(null);
    const {dispatch} = useNotifications();

    const api = new BackendApi(currentUser.getToken())

    async function onLoad() {
        setIsLoading(true);
        try {
            const repo = new CustomerBankAccountRepository(api)
            const accounts = await repo.getMyBankAccounts()

            setBankAccounts(accounts)
        } catch (error) {
            console.error('Error loading bank accounts:', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        void onLoad()
    }, []);

    useEffect(() => {
        if (!isAddCreditCardModalOpen && !isSavingsBalanceModalOpen) {
            return;
        }

        let cancelled = false;

        async function loadInstitutions() {
            try {
                const response = await api.GetBanksByCountryCode('nl');
                if (!cancelled) {
                    setInstitutions(response.banks.map((bank) => ({
                        id: bank.id,
                        name: bank.name,
                        logo: bank.logo ?? undefined,
                    })));
                }
            } catch {
                if (!cancelled) {
                    setInstitutions([]);
                }
            }
        }

        void loadInstitutions();

        return () => {
            cancelled = true;
        };
    }, [isAddCreditCardModalOpen, isSavingsBalanceModalOpen]);

    const operationalAccounts = bankAccounts.filter((account) => account.accountType === BankAccountType.OPERATIONAL);
    const creditCardAccounts = bankAccounts.filter((account) => account.accountType === BankAccountType.CREDIT_CARD);
    const savingsAccounts = bankAccounts.filter((account) => account.accountType === BankAccountType.SAVINGS);

    async function openSavingsPicker() {
        setIsSavingsPickerLoading(true);
        try {
            const response = await api.Business__relations__allRelations({});
            setRelations(toSelectableSavingsAccountRelations(response.relations, bankAccounts));
            setIsSavingsPickerOpen(true);
        } catch (error) {
            console.error('Error loading relations:', error);
            setRelations([]);
            dispatch({
                title: 'Spaarrekeningen laden mislukt',
                description: 'Er ging iets mis bij het ophalen van je bankrekeningen. Probeer het opnieuw.',
                type: 'error',
            });
        } finally {
            setIsSavingsPickerLoading(false);
        }
    }

    function onSavingsRelationSelected(relation: Relation) {
        setSelectedRelation(relation);
        setIsSavingsPickerOpen(false);
        setIsSavingsBalanceModalOpen(true);
    }

    async function onConfirmSavingsAccount(balanceCents: number, institutionName: string) {
        if (!selectedRelation) {
            return;
        }
        await api.BankAccount__addSavingsAccount({
            iban: selectedRelation.relation_iban,
            owner_name: selectedRelation.relation_name,
            balance_cents: balanceCents,
            institution_name: institutionName,
        });
        setIsSavingsBalanceModalOpen(false);
        setSelectedRelation(null);
        dispatch({
            title: 'Spaarrekening toegevoegd',
            description: 'Je spaarrekening is succesvol toegevoegd.',
            type: 'success',
        });
        await onLoad();
    }

    return (
        <Shell currentUser={currentUser} isLoading={isLoading} title={"Banksaldo's"}>
            <BanksPageSectionHeader title="Operationele bankrekeningen" />
            <div className="flex flex-wrap gap-8 items-start mb-10">
                {operationalAccounts.map((bankAccount) => (
                    <BankAccountCard
                        key={bankAccount.id}
                        customerBankAccount={bankAccount}
                        onRenewButtonClick={async (bankId) => {
                            setIsLoading(true);
                            const response = await api.InitiateBankConnection(
                                {
                                    bank_id: bankId,
                                    bank_account_id: bankAccount.id,
                                }
                            )
                            window.location.href = response.redirect_url
                        }}
                    />
                ))}
                <AddCardTile title="Voeg nog een bank toe" to="/connect-a-bank" />
            </div>

            <BanksPageSectionHeader title="Spaarrekeningen" />
            <div className="flex flex-wrap gap-8 items-start mb-10">
                {savingsAccounts.map((bankAccount) => (
                    <BankAccountCard
                        key={bankAccount.id}
                        customerBankAccount={bankAccount}
                    />
                ))}
                <AddCardTile
                    title="Voeg een spaarrekening toe"
                   
                    onClick={() => void openSavingsPicker()}
                    disabled={isSavingsPickerLoading}
                />
            </div>

            <SavingsAccountPickerDrawer
                open={isSavingsPickerOpen}
                relations={relations}
                onClose={() => setIsSavingsPickerOpen(false)}
                onSelect={onSavingsRelationSelected}
            />

            <AddSavingsAccountBalanceModal
                open={isSavingsBalanceModalOpen}
                relation={selectedRelation}
                institutions={institutions}
                onClose={() => {
                    setIsSavingsBalanceModalOpen(false);
                    setSelectedRelation(null);
                }}
                onConfirm={onConfirmSavingsAccount}
            />

            <BanksPageSectionHeader title="Creditcard" />
            <div className="flex flex-wrap gap-8 items-start">
                {creditCardAccounts.map((account) => (
                    <CreditCardAccountCard key={account.id} customerBankAccount={account} />
                ))}
                <AddCardTile title="Creditcard toevoegen" onClick={() => setIsAddCreditCardModalOpen(true)} />
            </div>

            <AddCreditCardModal
                open={isAddCreditCardModalOpen}
                institutions={institutions}
                existingCreditCards={creditCardAccounts}
                onClose={() => setIsAddCreditCardModalOpen(false)}
                onSubmit={async (payload) => {
                    await api.BankAccount__addCreditCard(payload)
                    await onLoad()
                }}
            />
        </Shell>
    )
}
